import React, { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";

const API_WRK = process.env.REACT_APP_API_WRK;

const allCategories = [
  { name: "Plumber", certification: "required" },
  { name: "Electrician", certification: "required" },
  { name: "Lawn Cleaning", certification: "not required" },
  { name: "Cleaning", certification: "not required" },
  { name: "Furniture Assembly", certification: "not required" },
  { name: "Roof Cleaning", certification: "optional" },
  { name: "Barber", certification: "required" },
  { name: "Haircut & Styling (Women)", certification: "required" },
  { name: "Pedicure & Manicure", certification: "required" },
  { name: "Facial & Skin Care", certification: "required" },
  { name: "Threading / Waxing", certification: "optional" },
  { name: "Makeup Artist (Event)", certification: "required" },
  { name: "Bridal Makeup", certification: "required" },
  { name: "At-Home Spa (Massage)", certification: "required" },

  { name: "Moving", certification: "not required" },
  { name: "Child Care", certification: "required" },
  { name: "Handyman", certification: "not required" },
  { name: "Junk Removal", certification: "not required" },
  { name: "Snow Removal", certification: "not required" },
  { name: "Window Cleaning", certification: "optional" },

  { name: "Painter", certification: "optional" },
  { name: "Event Photographer", certification: "optional" },
  { name: "Personal Chef", certification: "optional" },
  { name: "Carpet Cleaning", certification: "not required" },
  { name: "Pressure Washing", certification: "not required" },
  { name: "TV Mounting", certification: "not required" },
  { name: "Appliance Repair", certification: "optional" },
  { name: "Flooring Repair", certification: "optional" },
  { name: "Pool / Hot Tub Cleaning", certification: "optional" },

  { name: "Mechanic", certification: "required" },
  { name: "Car Detailing", certification: "not required" },
  { name: "Driver", certification: "required" },
  { name: "Respite Worker", certification: "required" },
  { name: "Mehndi", certification: "optional" },
  { name: "Tutor / Teaching", certification: "required" },
];

const Opportunities = () => {
  const workerId = localStorage.getItem("workerId");
  const [existingServices, setExistingServices] = useState([]);
  const [selected, setSelected] = useState([]);
  const [toolMap, setToolMap] = useState({});
  const [truckMap, setTruckMap] = useState({});
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (!workerId) return;
    axios
      .get(`${API_WRK}/profile/${workerId}`)
      .then((res) => {
        setExistingServices(res.data?.services || []);
      })
      .catch((err) => {
        console.error("Failed to fetch worker profile:", err);
      });
  }, [workerId]);

  const alreadyAppliedNames = existingServices.map((s) => s.name);
  const newOpportunities = allCategories.filter(
    (job) => !alreadyAppliedNames.includes(job.name)
  );

  const handleToggle = (name) => {
    setSelected((prev) =>
      prev.includes(name) ? prev.filter((i) => i !== name) : [...prev, name]
    );
    setMessage("");
  };

  const handleSave = async () => {
    if (!workerId) {
      setMessage("❌ Missing session. Please log in again.");
      return;
    }
    if (selected.length === 0) {
      setMessage("⚠️ Select at least one category to save.");
      return;
    }

    try {
      const newServices = selected.map((name) => ({
        name,
        certUrl: "",
        hasTools: !!toolMap[name],
        hasTruck: name === "Moving" ? !!truckMap[name] : false,
        status: "pending",
      }));

      const preservedServices = existingServices.filter(
        (s) => s.status === "approved" || s.status === "pending"
      );

      const finalServices = [
        ...preservedServices,
        ...newServices.filter((s) => !preservedServices.find((p) => p.name === s.name)),
      ];

      await axios.post(`${API_WRK}/save-services`, {
        workerId,
        services: finalServices,
      });

      setMessage("✅ Services updated! Please upload required certificates in 'Documents'.");
      setSelected([]);
      setToolMap({});
      setTruckMap({});
      // refresh existing services
      setExistingServices(finalServices);
    } catch (err) {
      console.error("Failed to update services:", err);
      setMessage(err?.response?.data?.message || "❌ Error updating services.");
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 50 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: "easeOut" }}
      className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-100 py-10 px-6"
    >
      <div className="max-w-4xl mx-auto">
        <h2 className="text-3xl font-bold text-blue-800 mb-6 text-center">
          Boost Opportunities – Select More Categories
        </h2>
        <p className="text-center text-gray-600 mb-8">
          Choose additional categories to get more jobs. Only new services are listed here.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-5">
          {newOpportunities.map((job, idx) => (
            <motion.div
              key={job.name}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.04 }}
              className={`p-4 rounded-xl cursor-pointer border shadow-md hover:shadow-lg transition select-none ${
                selected.includes(job.name) ? "bg-blue-100 border-blue-400" : "bg-white"
              }`}
              onClick={() => handleToggle(job.name)}
            >
              <div className="text-lg font-semibold">{job.name}</div>
              <div className="text-sm text-gray-600 mb-2">
                Certification: {job.certification}
              </div>

              {selected.includes(job.name) && (
                <div className="space-y-2 mt-2">
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={toolMap[job.name] || false}
                      onChange={(e) =>
                        setToolMap({ ...toolMap, [job.name]: e.target.checked })
                      }
                    />
                    I have the tools required
                  </label>

                  {job.name === "Moving" && (
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={truckMap[job.name] || false}
                        onChange={(e) =>
                          setTruckMap({ ...truckMap, [job.name]: e.target.checked })
                        }
                      />
                      I have a truck
                    </label>
                  )}
                </div>
              )}
            </motion.div>
          ))}
        </div>

        <div className="text-center mt-10">
          <button
            onClick={handleSave}
            className="px-6 py-3 rounded-full bg-blue-600 hover:bg-blue-700 text-white font-bold shadow-lg"
          >
            Save Selected Categories
          </button>
          {message && (
            <p
              className={`mt-4 text-center text-sm font-medium ${
                message.startsWith("✅") ? "text-green-700" : "text-red-600"
              }`}
            >
              {message}
            </p>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default Opportunities;
